<template>
  <div>
    <div class="card my-4 g-card">
      <div class="card-body px-md-5 shadow-sm">
        <div class="row">
          <div class="col-md-6 py-2">
            <h5 class="mb-4 main-title">
              {{ $t("reservation.yourReservation") }}
            </h5>
            <table class="table custom-table">
              <tr>
                <td>{{ $t("reservation.totalNumberOfRooms") }}</td>
                <td class="text-right">{{ checkOutItem.quantity }}</td>
              </tr>
              <tr>
                <td>{{ $t("reservation.totalNumberOfPersons") }}</td>
                <td class="text-right">{{ checkOutItem.guests }}</td>
              </tr>
              <tr>
                <td>{{ $t("reservation.nights") }}</td>
                <td class="text-right">{{ nightsCount }}</td>
              </tr>
            </table>
          </div>
          <div class="col-md-6 py-2">
            <h5 class="mb-4 main-title">
              {{ $t("reservation.travelPeriod") }}
            </h5>
            <table class="table custom-table">
              <tr>
                <td>{{ $t("products.checkIn") }}</td>
                <td class="text-right">
                  {{ formatData(checkoutData.date.checkIn) }}
                </td>
              </tr>
              <tr>
                <td>{{ $t("reservation.checkOut") }}</td>
                <td class="text-right">
                  {{ formatData(checkoutData.date.checkOut) }}
                </td>
              </tr>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="card my-4 g-card">
      <div class="card-body px-md-5 shadow-sm">
        <div class="d-md-block d-none">
          <table class="table">
            <tr>
              <th>{{ $t("reservation.noOfRooms") }}</th>
              <th>{{ $t("reservation.totalPrice") }}</th>
              <th class="text-center">{{ $t("property.personPerRoom") }}</th>
              <th>{{ $t("reservation.mealType") }}</th>
              <th>{{ $t("reservation.pricePerRoom") }}</th>
              <th>{{ $t("reservation.total") }}</th>
              <th>{{ $t("reservation.remove") }}</th>
            </tr>
            <tr
              v-for="(item, index) in checkoutData.cart"
              :key="`${item.id}-${index}`"
            >
              <td>{{ item.quantity }}x</td>
              <td class="text-main">
                <span>{{ rateDetails(item.product, true) }}</span
                ><br />
                <div>
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      rateDetailsText(item.product)
                    "
                    >{{ $t("property.rateDetails") }}</span
                  >
                  |
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      cancellationPolicyText(item.product)
                    "
                    >{{ $t("property.cancellationPolicy") }}</span
                  >
                </div>
              </td>
              <td class="text-center">
                <GuestIcons :persons="item.guests" />
              </td>
              <td>
                <template v-if="item.product.rate.mealIncluded">
                  <span>{{ $t("reservation.mealIncluded") }}</span>
                </template>
                <template v-else>
                  {{ $t("property.noMealIncluded") }}
                </template>
              </td>
              <td>
                <span class="text-main"
                  >{{ item.product.currency }}
                  {{ item.price | formatPrice }}</span
                >
              </td>
              <td>
                <span class="text-main"
                  ><b
                    >{{ item.product.currency }}
                    {{ (item.price * item.quantity) | formatPrice }}</b
                  ></span
                >
              </td>
              <td class="text-center">
                <img
                  @click="removeRoom()"
                  src="../../assets/icons/trash.svg"
                  class="mr-1 cursor-pointer"
                  alt="trash"
                />
              </td>
            </tr>
          </table>
        </div>
        <div class="d-md-none d-block">
          <table
            class="table"
            v-for="(item, index) in checkoutData.cart"
            :key="`${item.id}-${index}`"
          >
            <tr>
              <td class="text-main">
                <span>{{ rateDetails(item.product, true) }}</span
                ><br />
                <div>
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      rateDetailsText(item.product)
                    "
                    >{{ $t("property.rateDetails") }}</span
                  >
                  |
                  <span
                    role="button"
                    v-b-popover.bottom.hover.html="
                      cancellationPolicyText(item.product)
                    "
                    >{{ $t("property.cancellationPolicy") }}</span
                  >
                </div>
              </td>
              <td class="text-right">
                <img
                  @click="removeRoom()"
                  src="../../assets/icons/trash.svg"
                  class="mr-1 cursor-pointer"
                  alt="trash"
                />
              </td>
            </tr>
            <tr>
              <td>{{ $t("reservation.noOfRooms") }}</td>
              <td class="text-right">{{ item.quantity }}x</td>
            </tr>
            <tr>
              <td>{{ $t("property.personPerRoom") }}</td>
              <td class="text-right">
                <GuestIcons :persons="item.guests" />
              </td>
            </tr>
            <tr>
              <td>{{ $t("reservation.mealType") }}</td>
              <td class="text-right">
                <template v-if="item.product.rate.mealIncluded">
                  <span>{{ $t("reservation.mealIncluded") }}</span>
                </template>
                <template v-else>
                  {{ $t("property.noMealIncluded") }}
                </template>
              </td>
            </tr>
            <tr>
              <td>{{ $t("reservation.pricePerRoom") }}</td>
              <td class="text-right">
                <span class="text-main"
                  >{{ item.product.currency }}
                  {{ item.price | formatPrice }}</span
                >
              </td>
            </tr>
            <tr>
              <td>{{ $t("reservation.total") }}</td>
              <td class="text-right">
                <span class="text-main"
                  ><b
                    >{{ item.product.currency }}
                    {{ (item.price * item.quantity) | formatPrice }}</b
                  ></span
                >
              </td>
            </tr>
          </table>
        </div>
      </div>
    </div>

    <ValidationObserver ref="observer">
      <div class="card my-4 g-card" ref="bookerDetails">
        <div class="card-body px-md-5 shadow-sm">
          <h5 class="mb-4 main-title">{{ $t("reservation.yourDetails") }}</h5>
          <div class="row">
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="booker first name"
                v-model="bookerDetails.firstName"
                :placeholder="$t('reservation.placeholderFirstName')"
                rules="required"
                @blur="guestDetails.firstName = bookerDetails.firstName"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="booker last name"
                v-model="bookerDetails.lastName"
                :placeholder="$t('reservation.placeholderLastName')"
                rules="required"
                @blur="guestDetails.lastName = bookerDetails.lastName"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="booker email"
                type="email"
                v-model="bookerDetails.email"
                :placeholder="$t('reservation.placeholderEmail')"
                rules="required|email"
                @blur="guestDetails.email = bookerDetails.email"
              />
            </div>
            <div class="col-md-5 my-3 no-border">
              <ValidatedInput
                name="booker phone"
                type="tel"
                :placeholder="$t('reservation.placeholderPhone')"
                v-model="bookerDetails.phone"
                @blur="guestDetails.phone = bookerDetails.phone"
                rules="required"
              />
            </div>
          </div>
        </div>
      </div>

      <div class="card my-4 g-card">
        <div class="card-body px-md-5 shadow-sm">
          <h5 class="mb-4 main-title">{{ $t("reservation.guestDetails") }}</h5>
          <div class="row">
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="guest first name"
                v-model="guestDetails.firstName"
                :placeholder="$t('reservation.placeholderFirstName')"
                rules="required"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="guest last name"
                v-model="guestDetails.lastName"
                :placeholder="$t('reservation.placeholderLastName')"
                rules="required"
              />
            </div>
            <div class="col-md-5 my-3">
              <ValidatedInput
                name="guest email"
                type="email"
                v-model="guestDetails.email"
                :placeholder="$t('reservation.placeholderEmail')"
                rules="required|email"
              />
            </div>
            <div class="col-md-5 my-3 no-border">
              <ValidatedInput
                name="guest phone"
                type="tel"
                :placeholder="$t('reservation.placeholderPhone')"
                v-model="guestDetails.phone"
                rules="required"
              />
            </div>
            <div class="col-md-10 my-3">
              <ValidatedInput
                type="textarea"
                v-model="guestDetails.remarks"
                placeholder="Do you have any remarks?"
                rules=""
              />
            </div>
          </div>
        </div>
      </div>

      <div class="card my-4 g-card">
        <div class="card-body px-md-5 shadow-sm">
          <h5 class="mb-4 main-title">
            {{ $t("reservation.paymentDetails") }}
          </h5>
          <div class="row">
            <div class="col-12" v-if="prebookLoading">
              <Spinner small />
            </div>
            <div v-else class="col-md-10 my-3">
              <div id="nuiteePaymentContainer"></div>
            </div>
          </div>
        </div>
      </div>

      <hr />

      <TermsAndPolicies />
    </ValidationObserver>
  </div>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import Spinner from "../Widgets/Spinner.vue";
import GuestIcons from "../Widgets/GuestIcons.vue";
import TermsAndPolicies from "./TermsAndPolicies.vue";
import {
  rateDetails,
  rateDetailsText,
  cancellationPolicyText,
} from "../../helpers/nuitee";
import { loadScript } from "../../helpers/loadScript";
export default {
  name: "NuiteeCheckout",
  props: {
    checkoutData: {
      type: Object,
      required: true,
    },
    collectCardDetails: {
      type: Boolean,
      required: true,
    },
  },
  components: {
    Spinner,
    GuestIcons,
    TermsAndPolicies,
  },
  data() {
    return {
      listId: null,
      prebookLoading: true,
      bookerDetails: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
      },
      guestDetails: {
        firstName: "",
        lastName: "",
        phone: "",
        email: "",
        remarks: "",
        occupancyNumber: this.checkOutItem?.guests || 1,
      },
    };
  },
  computed: {
    ...mapGetters("config", ["bookingEngineId"]),
    checkOutItem() {
      return this.checkoutData.cart[0];
    },
    nightsCount() {
      const { checkIn, checkOut } = this.checkoutData.date;
      return moment(checkOut, "YYYY-MM-DD").diff(
        moment(checkIn, "YYYY-MM-DD"),
        "days"
      );
    },
  },
  created() {
    this.listId = this.bookingEngineId;
  },
  async mounted() {
    try {
      this.prebookLoading = true;
      await loadScript(
        "https://payment-wrapper.liteapi.travel/dist/liteAPIPayment.js?v=a1"
      );
      const { secretKey, transactionId, prebookId } = await this.nuiteePrebook({
        offer_id: this.checkOutItem.product.offerId,
      });
      const { id, name } = this.checkoutData.property;
      const origin = window.location.origin;
      const liteAPIPayment = new window.LiteAPIPayment({
        secretKey,
        publicKey: "live",
        appearance: { theme: "flat" },
        options: { business: { name } },
        targetElement: "#nuiteePaymentContainer",
        returnUrl: `${origin}/payment-processing?condition=nuitee-completion&transactionId=${transactionId}&propertyId=${id}&prebookId=${prebookId}&list_id=${this.listId}`,
      });
      liteAPIPayment.handlePayment();
    } catch (error) {
      console.log(error.message);
    } finally {
      this.prebookLoading = false;
    }
  },
  methods: {
    ...mapActions("booking", ["nuiteePrebook"]),
    rateDetails,
    rateDetailsText,
    cancellationPolicyText,
    removeRoom() {
      const { id } = this.$route.params;
      sessionStorage.removeItem(`checkoutData-${id}`);
      this.$router.push(`/listing/${id}`);
    },
    setCountryCode(section, { dialCode }) {
      this[section].countryAccessCode = dialCode;
    },
    getData() {
      const { bookerDetails, guestDetails } = this;
      return {
        bookerDetails,
        guestDetails,
      };
    },
    formatData(date) {
      return moment(date).format("ddd, DD MMM YYYY");
    },
  },
};
</script>

<style scoped>
table tr td {
  font-size: 16px;
}

@media screen and (max-width: 600px) {
  h5 {
    font-size: 16px;
  }
  table tr td {
    font-size: 14px;
  }
}
</style>
